import { Button, Tooltip, useOpenModal } from '@modusign/design-system';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import UploadModal from '@app/components/Modal/UploadModal';
import LoadingProgressBar from '@app/components/Modal/UploadModal/LoadingProgressBar';
import useStartContractLink from '@app/features/contractLink/contractLinkCreate/hooks/useStartContractLink';
import useUploadDocumentFile from '@app/hooks/documentSetup/useUploadDocumentFile';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';
import MeansType from '@app/store/MeansType';
import { modalOpen } from '@app/store/modal/actions';
import { setTemplateParticipants } from '@app/store/template/actions';

import { contractLinkCreateLoadPdfSuccess } from '../../actions';
import CreateLinkErrorModal from '../CreateLinkErrorModal';

export default function ContractLinkCreateButton() {
  const dispatch = useDispatch();
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const openModal = useOpenModal();

  const { isLoading: isFileUploading, mutate: uploadDocumentFile } = useUploadDocumentFile({
    onError: (error) => {
      dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
    },
    onSuccess: ({ fileId, pdfArrayBuffer, url, title }) => {
      dispatch(contractLinkCreateLoadPdfSuccess({ fileId, pdfArrayBuffer, title, url }));
      dispatch(setTemplateParticipants([{ id: v4(), role: '', meansType: MeansType.SECURE_LINK }]));
      dispatch(
        modalOpen('CONTRACT_LINK_START_CONFIRM_MODAL', {
          initializeDocumentRenderTree: true,
        })
      );
    },
  });

  const { isLoading: isContractLinkCreateLoading, startContractLink } = useStartContractLink();

  const { isPermitted } = useMemberPermissions('MANAGE_CONTRACT_LINK');

  const onStartContractLink = useCallback(async () => {
    try {
      dispatch(clearDocumentSetupState());
      await startContractLink();
      setIsUploadModalOpen(true);
    } catch (e) {
      openModal(({ onClose }) => (
        <CreateLinkErrorModal error={e} hasLink={false} onConfirm={onClose} />
      ));
    }
  }, [dispatch, openModal, startContractLink]);

  return (
    <>
      <Tooltip content="권한 없음" disabled={isPermitted} placement="bottom">
        <Button
          data-testid="contract-link-create-button"
          disabled={!isPermitted}
          display={['none', null, 'inline-block']}
          weight="light"
          onClick={() => {
            pushDataLayer({ event: 'clickCreateLinkSigning' });
            onStartContractLink();
          }}
        >
          + 링크서명 시작
        </Button>
      </Tooltip>
      <UploadModal
        isOpen={isUploadModalOpen}
        needDraft
        needTemplate
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={(file) => {
          setIsUploadModalOpen(false);
          setFileName(file.name);
          uploadDocumentFile(file);
        }}
      />
      <LoadingProgressBar
        extension={fileName ? getExtensionFromFileName(fileName) : null}
        isLoading={isFileUploading}
      />
      {isContractLinkCreateLoading && <GlobalLoadingSpinner />}
    </>
  );
}
